import {
  createFrontendModule,
  featureFlagsApiRef,
} from '@backstage/frontend-plugin-api';
import type { ApiHolder } from '@backstage/frontend-plugin-api';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';

/**
 * Per-user flag, toggled at `/settings/feature-flags`. Off by default, and
 * read once while the extension tree is built - turning it on or off takes a
 * page reload, the same as every other feature flag in the frontend system.
 */
export const ENTITY_CARDS_FLAG = 'catalog-graph-entity-cards';

const entityCardsEnabled = (apis: ApiHolder) =>
  apis.get(featureFlagsApiRef)?.isActive(ENTITY_CARDS_FLAG) ?? false;

/**
 * Both graph surfaces render the same `EntityRelationsGraph`, which draws a
 * node with whatever `renderNode` returns and falls back to its own pill when
 * that prop is absent. Neither the card nor the page extension exposes the
 * prop through config, so the flag is applied by replacing the extension's
 * loader - and when it is off, by calling the original factory untouched, so
 * what renders is upstream's own component with upstream's own nodes.
 *
 * The overrides are declared here rather than through
 * `catalogGraphPlugin.withOverrides()` because `app.packages: all` discovers
 * the plugin, `createApp` puts discovered features ahead of the ones passed
 * to it, and duplicate extension IDs resolve first-wins - so an overridden
 * copy of the plugin would lose to the one discovery already installed. A
 * module's extensions are matched to the plugin's by ID and replace them.
 */
export const relationsCard = catalogGraphPlugin
  .getExtension('entity-card:catalog-graph/relations')
  .override({
    factory(originalFactory, { apis, config }) {
      if (!entityCardsEnabled(apis)) {
        return originalFactory();
      }

      // `filter` and `type` are the entity-card blueprint's own config, read
      // by its factory to decide which entities get the card and which lane
      // of the overview it sits in. Only the rest describes the graph.
      const { filter, type, ...graphConfig } = config;

      return originalFactory({
        params: {
          loader: async () => {
            const [{ EntityCatalogGraphCard }, { EntityCardNode }] =
              await Promise.all([
                import('@backstage/plugin-catalog-graph'),
                import('./EntityCardNode'),
              ]);
            return (
              <EntityCatalogGraphCard
                {...graphConfig}
                renderNode={EntityCardNode}
              />
            );
          },
        },
      });
    },
  });

export const graphPage = catalogGraphPlugin
  .getExtension('page:catalog-graph')
  .override({
    factory(originalFactory, { apis, config }) {
      if (!entityCardsEnabled(apis)) {
        return originalFactory();
      }

      // `path` and `title` belong to the page blueprint, which applies them
      // itself; the page component reads the rest twice over, once as props and
      // once as the initial state of its filter bar.
      const { path, title, ...pageConfig } = config;

      return originalFactory({
        params: {
          loader: async () => {
            const [{ CatalogGraphPage }, { EntityCardNode }] =
              await Promise.all([
                import('@backstage/plugin-catalog-graph'),
                import('./EntityCardNode'),
              ]);
            return (
              <CatalogGraphPage
                {...pageConfig}
                initialState={pageConfig}
                renderNode={EntityCardNode}
              />
            );
          },
        },
      });
    },
  });

export const catalogGraphModule = createFrontendModule({
  pluginId: 'catalog-graph',
  featureFlags: [
    {
      name: ENTITY_CARDS_FLAG,
      description:
        'Draw relations-graph nodes as entity cards - kind, title and the first few spec fields - instead of the default pill',
    },
  ],
  extensions: [relationsCard, graphPage],
});
